import { ValidateImporterTimesheetRowDocument } from '@oc/pages/importers_rsi/validations/data/__generated__/ValidateImporterTimesheetRow.graphql';
import { useCallback, useRef } from 'react';
import { useTypedQuery } from 'utils/hooks/useTypedQuery';
import { useImportersIntl } from '../../../translations/useImportersIntl';
import { RsiData, RsiRowOnErrorCallback, RsiRowValidator } from '../../../Types';
import { isInvalidField, sanitizeRsiArrayData, sanitizeRsiData } from '../../../validations/impl/validationUtils';

export function useValidateRowTimesheet(): RsiRowValidator {
  const { get } = useValidateTimesheetRowApi();
  const { invalidField } = useImportersIntl();

  const getRef = useRef(get);
  getRef.current = get;
  const invalidFieldRef = useRef(invalidField);
  invalidFieldRef.current = invalidField;

  return useCallback(async (row: RsiData, addError: RsiRowOnErrorCallback, table: RsiData[]) => {
    const entriesToValidate = findRelativeEntries(row, table);
    const response = await getRef.current({
      variables: {
        input: {
          values: entriesToValidate.map(([index, data]) => ({
            index,
            urnToValueMap: sanitizeRsiData(data, { allow: 'all' }),
            urnToValuesMap: sanitizeRsiArrayData(data, { allow: 'all' })
          }))
        }
      }
    });
    response.data?.result.validations?.forEach((item) => {
      if (isInvalidField(item)) {
        addError(
          item.urn,
          { level: 'error', message: item.result.message ?? invalidFieldRef.current },
          item.index ?? 0
        );
      } else if (!!item?.urn) {
        addError(item.urn, null, item.index ?? 0);
      }
    });
  }, []);
}

export function useValidateTimesheetRowApi() {
  return useTypedQuery(ValidateImporterTimesheetRowDocument);
}

const ENGAGEMENT_ID_KEY = 'urn:utmost:*:timesheet:fieldpaths:engagement.engagementId';
const PERSON_ID_KEY = 'urn:utmost:*:timesheet:fieldpaths:person.uid';
const START_DATE_KEY = 'urn:utmost:*:timesheet:fieldpaths:startDate';
const END_DATE_KEY = 'urn:utmost:*:timesheet:fieldpaths:endDate';

function findRelativeEntries(row: RsiData, table: RsiData[]): [index: number, data: RsiData][] {
  const engagementId = row[ENGAGEMENT_ID_KEY];
  const personId = row[PERSON_ID_KEY];
  const startDate = row[START_DATE_KEY];
  const endDate = row[END_DATE_KEY];

  return table.reduce((acc: [number, RsiData][], data, index) => {
    if (
      data[ENGAGEMENT_ID_KEY] === engagementId &&
      data[PERSON_ID_KEY] === personId &&
      data[START_DATE_KEY] === startDate &&
      data[END_DATE_KEY] === endDate
    ) {
      acc.push([index, data]);
    }
    return acc;
  }, []);
}
